import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../../components/admin/AdminShell';
import { useWACampaign, useWACampaignMessages, useSendWACampaign, useRetryWAFailed } from '../../../hooks/useWhatsAppQueries';
import { toast } from 'sonner';
import { ArrowLeft, Send, RefreshCw, CheckCheck, Clock, XCircle, Eye, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';

const STATUS_CONFIG = {
  pending:   { icon: Clock,       color: 'text-[#B9B9B9]',  label: 'Pending' },
  sent:      { icon: Send,        color: 'text-[#D4A843]',  label: 'Sent' },
  delivered: { icon: CheckCheck,  color: 'text-[#4ADE80]',  label: 'Delivered' },
  read:      { icon: Eye,         color: 'text-[#25D366]',  label: 'Read' },
  failed:    { icon: XCircle,     color: 'text-[#FF4D4D]',  label: 'Failed' },
};

const CAMPAIGN_STATUS = {
  draft:    'bg-[rgba(255,255,255,0.06)] text-[#B9B9B9]',
  sending:  'bg-[rgba(212,168,67,0.15)] text-[#D4A843]',
  sent:     'bg-[rgba(74,222,128,0.12)] text-[#4ADE80]',
  partial:  'bg-[rgba(251,191,36,0.12)] text-[#FBB924]',
  failed:   'bg-[rgba(255,77,77,0.12)] text-[#FF4D4D]',
};

export default function WhatsAppCampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useWACampaign(id);
  const [msgFilter, setMsgFilter] = useState('all');
  const { data: messagesData } = useWACampaignMessages(id, msgFilter !== 'all' ? { status: msgFilter } : {});
  const sendCampaign  = useSendWACampaign();
  const retryFailed   = useRetryWAFailed();
  const messages = messagesData?.messages || [];

  const handleSend = async () => {
    try {
      await sendCampaign.mutateAsync(id);
      toast.success('Campaign is now sending…');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start campaign');
    }
  };

  const handleRetry = async () => {
    try {
      const r = await retryFailed.mutateAsync(id);
      toast.success(`Retrying ${r.data?.contacts || 0} failed messages…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    }
  };

  if (isLoading) return (
    <AdminShell title="WhatsApp Campaign">
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={22} className="animate-spin text-[#25D366]" />
      </div>
    </AdminShell>
  );

  if (!campaign) return (
    <AdminShell title="Not Found">
      <p className="text-[#B9B9B9]">Campaign not found.</p>
    </AdminShell>
  );

  const deliveryRate = campaign.sent_count > 0
    ? Math.round((campaign.delivered_count / campaign.sent_count) * 100)
    : 0;
  const readRate = campaign.delivered_count > 0
    ? Math.round((campaign.read_count / campaign.delivered_count) * 100)
    : 0;

  return (
    <AdminShell title={campaign.name}>
      {/* Header */}
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <button onClick={() => navigate('/admin/marketing/whatsapp')} className="text-sm text-[#B9B9B9] hover:text-[#F2F2F2] flex items-center gap-1 mb-3">
            <ArrowLeft size={14}/> Back
          </button>
          <h1 className="text-2xl font-bold text-[#F2F2F2]">{campaign.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <code className="text-xs text-[#D4A843] bg-[rgba(212,168,67,0.08)] px-2 py-0.5 rounded">{campaign.template_name}</code>
            <span className="text-xs text-[#6B6B6B]">lang: {campaign.language_code}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${CAMPAIGN_STATUS[campaign.status] || CAMPAIGN_STATUS.draft}`}>
              {campaign.status}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {(campaign.status === 'draft' || campaign.status === 'failed') && (
            <Button onClick={handleSend} disabled={sendCampaign.isPending} style={{background:'#25D366',color:'#000'}} className="gap-2 rounded-xl" data-testid="wa-detail-send-btn">
              <Send size={15}/> {sendCampaign.isPending ? 'Starting…' : 'Send Campaign'}
            </Button>
          )}
          {campaign.failed_count > 0 && (
            <Button onClick={handleRetry} disabled={retryFailed.isPending} variant="outline" className="gap-2 rounded-xl bg-transparent border-[rgba(255,77,77,0.3)] text-[#FF4D4D]">
              <RotateCcw size={15}/> {retryFailed.isPending ? 'Retrying…' : `Retry ${campaign.failed_count} Failed`}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total Contacts', value: campaign.total_contacts || 0, color: '#B9B9B9' },
          { label: 'Sent',       value: campaign.sent_count || 0,      color: '#D4A843' },
          { label: 'Delivered',  value: campaign.delivered_count || 0, color: '#4ADE80', sub: `${deliveryRate}%` },
          { label: 'Read',       value: campaign.read_count || 0,      color: '#25D366', sub: `${readRate}% of delivered` },
          { label: 'Failed',     value: campaign.failed_count || 0,    color: '#FF4D4D' },
        ].map(s => (
          <Card key={s.label} className="p-5 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.07)] rounded-xl">
            <p className="text-xs text-[#6B6B6B] mb-1">{s.label}</p>
            <p className="text-2xl font-bold font-mono" style={{color: s.color}}>{s.value}</p>
            {s.sub && <p className="text-[10px] text-[#6B6B6B] mt-0.5">{s.sub}</p>}
          </Card>
        ))}
      </div>

      {/* Message Log */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#F2F2F2]">Message Log</h2>
          <div className="flex gap-2">
            {['all','sent','delivered','read','failed'].map(s => (
              <button
                key={s}
                onClick={() => setMsgFilter(s)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                  msgFilter === s ? 'bg-[#25D366] text-black' : 'bg-[rgba(255,255,255,0.05)] text-[#B9B9B9] hover:bg-[rgba(255,255,255,0.09)]'
                }`}
              >
                {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="text-center py-12 text-[#6B6B6B] text-sm">
            {campaign.status === 'draft' ? 'No messages yet — start the campaign to begin sending.' : 'No messages match this filter.'}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[rgba(255,255,255,0.07)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)]">
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Phone</th>
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Status</th>
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Message ID</th>
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Sent At</th>
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Delivered At</th>
                  <th className="px-5 py-3 text-left text-xs text-[#6B6B6B] uppercase tracking-widest">Error</th>
                </tr>
              </thead>
              <tbody>
                {messages.map(m => {
                  const sc = STATUS_CONFIG[m.status] || STATUS_CONFIG.pending;
                  const Icon = sc.icon;
                  return (
                    <tr key={m._id} className="border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                      <td className="px-5 py-3 font-mono text-[#F2F2F2] text-xs">
                        {m.to_number ? `${'*'.repeat(m.to_number.length - 4)}${m.to_number.slice(-4)}` : '—'}
                      </td>
                      <td className="px-5 py-3">
                        <div className={`flex items-center gap-1.5 ${sc.color}`}>
                          <Icon size={13}/>
                          <span className="text-xs font-medium">{sc.label}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <code className="text-[10px] text-[#6B6B6B] truncate block max-w-[160px]">{m.wa_message_id || '—'}</code>
                      </td>
                      <td className="px-5 py-3 text-xs text-[#B9B9B9]">{m.sent_at ? new Date(m.sent_at).toLocaleString() : '—'}</td>
                      <td className="px-5 py-3 text-xs text-[#B9B9B9]">{m.delivered_at ? new Date(m.delivered_at).toLocaleString() : '—'}</td>
                      <td className="px-5 py-3 text-xs text-[#FF4D4D] max-w-[180px] truncate">{m.error_message || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
